import {
  FormControl,
  FormErrorMessage,
  Flex,
} from "@chakra-ui/react";
import { FormProvider, useForm } from "react-hook-form";
import PrimaryButton from "../PrimaryButton";
import { useMutation, useQueryClient } from "react-query";
import { createUser } from "../../mutations/user.mutations";
import Select from "../inputs/Select";
import Input from "../inputs/Input";
import { useSimpleToast } from "../../hooks/toast.hooks";
import { sendInvitations } from "../../mutations/center.mutations";
import { MutateErrors } from "../../lib/generic";

type CreateUserFormProps = {
  centerId?: number;
  onSubmit?: () => void;
};

function CreateUserForm({ centerId, onSubmit }: CreateUserFormProps) {
  const methods = useForm();
  const queryClient = useQueryClient();
  const toast = useSimpleToast();
  const { mutate: mutateSendInvitations } = useMutation(sendInvitations, {
    onSuccess: () => {
      queryClient.invalidateQueries("users");
      if (onSubmit) {
        onSubmit();
      }
    }
  });
  const { mutate: mutateCreateUser } = useMutation(createUser, {
    onSuccess: () => {
      queryClient.invalidateQueries("users");
      if (onSubmit) {
        onSubmit();
      }
    },
    onError: (err: MutateErrors) => {
      toast.error('Ajout utilisateur', "Echec de l'ajout de l'utilisateur. Est-ce que l'adresse email est déjà utilisée ?")
      err.response.data.errors.forEach((error) => {
        methods.setError(error.field, {
          message: error.message,
        });
      });
    },
  });

  const handleSubmit = async () => {
    try {
      const values = await methods.getValues();
      if (centerId) {
        // mutateCreateUser({ user: values, centerId, invitation: true });
        mutateSendInvitations({ center: { id: centerId }, users: [values] });
      } else {
        mutateCreateUser({ user: values, invitation: true });
      }
    } catch (err) {
      // Todo: Sentry
    }
  };

  return (
    <FormProvider {...methods}>
      <form style={{ marginTop: "32px", marginBottom: "32px" }}>
        <Flex direction="column" gap="24px">
          <Flex gap="24px">
            <FormControl isInvalid={!!methods.formState.errors.firstName}>
              <Input
                id="firstName"
                placeholder="Prénom"
                {...methods.register("firstName", {
                  required: "Ce champ est obligatoire",
                })}
              ></Input>
              <FormErrorMessage>
                <>{methods.formState.errors?.firstName?.message}</>
              </FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!methods.formState.errors.lastName}>
              <Input
                id="lastName"
                placeholder="Nom"
                {...methods.register("lastName", {
                  required: "Ce champ est obligatoire",
                })}
              ></Input>
              <FormErrorMessage>
                <>{methods.formState.errors?.lastName?.message}</>
              </FormErrorMessage>
            </FormControl>
          </Flex>
          <FormControl isInvalid={!!methods.formState.errors.email}>
            <Input
              id="email"
              placeholder="Email"
              {...methods.register("email", {
                required: "Ce champ est obligatoire",
              })}
            ></Input>
            <FormErrorMessage>
              <>{methods.formState.errors?.email?.message}</>
            </FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={!!methods.formState.errors.phoneNumber}>
            <Input
              id="phoneNumber"
              placeholder="Téléphone"
              {...methods.register("phoneNumber")}
            ></Input>
            <FormErrorMessage>
              <>{methods.formState.errors?.phoneNumber?.message}</>
            </FormErrorMessage>
          </FormControl>

          {centerId !== undefined && (
            <FormControl isInvalid={!!methods.formState.errors.role}>
              <Select
                id="role"
                placeholder="Rôle"
                defaultValue={"AUDIOPROSTHESIST"}
                {...methods.register("role", {
                  required: "Ce champ est obligatoire",
                })}
              >
                <option value="AUDIOPROSTHESIST">Audioprothésiste</option>
                <option value="ASSISTANT">Assistant</option>
                <option value="OTHER">Autre</option>
              </Select>
              <FormErrorMessage>
                <>{methods.formState.errors?.role?.message}</>
              </FormErrorMessage>
            </FormControl>
          )}

          {centerId !== undefined && (
            <FormControl isInvalid={!!methods.formState.errors.access}>
              <Select
                id="access"
                placeholder="Accès"
                defaultValue="ADMIN"
                {...methods.register("access", {
                  required: "Ce champ est obligatoire",
                })}
              >
                <option value="ADMIN">Administrateur du centre</option>
                <option value="MEMBER">Membre</option>
              </Select>
              <FormErrorMessage>
                <>{methods.formState.errors?.access?.message}</>
              </FormErrorMessage>
            </FormControl>
          )}

          <PrimaryButton type="button" onClick={handleSubmit}>
            Sauvegarder
          </PrimaryButton>
        </Flex>
      </form>
    </FormProvider>
  );
}

export default CreateUserForm;
